import type { HydratedAsset } from "@zapier/client";
import type { AssetTypes } from "@zapier/utils";

export interface CopyCandidateAsset {
  id?: string;
  type?: AssetTypes;
}

interface ResolveSelectedCopyAssetsInput {
  assetType?: AssetTypes;
  assetsForCopyToAnotherAccount?: CopyCandidateAsset[];
  selectedTypedAssetIdsForCopy: string[];
}

/** Strip a legacy `zap:` prefix from raw Zap IDs before lookup. */
export function normalizeAssetId(assetId: string) {
  return assetId.startsWith("zap:") ? assetId.slice(4) : assetId;
}

function getRawIdFromTypedId(typedId: string) {
  const separatorIndex = typedId.indexOf(":");
  return separatorIndex === -1 ? typedId : typedId.slice(separatorIndex + 1);
}

export function toHydratedAsset(
  asset: CopyCandidateAsset,
  fallbackAssetType?: AssetTypes,
) {
  const assetType = asset.type ?? fallbackAssetType;
  if (!assetType || typeof asset.id !== "string" || asset.id.length === 0) {
    return;
  }

  return {
    ...asset,
    type: assetType,
  } as HydratedAsset;
}

export function resolveSelectedCopyAssets({
  assetType,
  assetsForCopyToAnotherAccount,
  selectedTypedAssetIdsForCopy,
}: ResolveSelectedCopyAssetsInput) {
  const assets = assetsForCopyToAnotherAccount ?? [];
  if (assets.length === 0 || selectedTypedAssetIdsForCopy.length === 0) {
    return [] as HydratedAsset[];
  }

  const hydratedAssets = assets
    .map((asset) => toHydratedAsset(asset, assetType))
    .filter((asset): asset is HydratedAsset => !!asset);

  const assetsByTypedId = new Map<string, HydratedAsset>();
  const assetsByRawId = new Map<string, HydratedAsset>();

  for (const asset of hydratedAssets) {
    const normalizedId = normalizeAssetId(asset.id);
    assetsByRawId.set(asset.id, asset);
    assetsByRawId.set(normalizedId, asset);
    assetsByTypedId.set(`${asset.type}:${normalizedId}`, asset);
  }

  const selectedAssets: HydratedAsset[] = [];
  const seenAssetKeys = new Set<string>();

  for (const typedId of selectedTypedAssetIdsForCopy) {
    const normalizedId = normalizeAssetId(getRawIdFromTypedId(typedId));
    const selectedAsset =
      assetsByTypedId.get(typedId) ??
      assetsByRawId.get(typedId) ??
      assetsByRawId.get(normalizedId);

    if (!selectedAsset) {
      continue;
    }

    const assetKey = `${selectedAsset.type}:${selectedAsset.id}`;
    if (seenAssetKeys.has(assetKey)) {
      continue;
    }

    seenAssetKeys.add(assetKey);
    selectedAssets.push(selectedAsset);
  }

  return selectedAssets;
}
